import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { supplierReportQuery } from "@/app/Reports/SupplierReport/GraphQL/supplierReportQuery";
import { useSearch } from "@tanstack/react-router";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { ResultOf } from "@graphql-typed-document-node/core";
import { Ref, useMemo } from "react";
import { ColDef, RowSelectionOptions, themeQuartz } from "ag-grid-community";
import dayjs from "dayjs";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { Currency } from "@/gql/graphql";
import { cn } from "@/lib/utils";

type SupplierReportListItem = ResultOf<
  typeof supplierReportQuery
>["supplierReport"][number];

const imoovaTheme = themeQuartz.withParams({
  browserColorScheme: "light",
  fontFamily: {
    googleFont: "Inter",
  },
  fontSize: 12,
  headerFontSize: 14,
  spacing: 5,
});

const defaultColDef = {
  floatingFilter: true,
};

function RevenuePerBookingCell({
  data,
}: CustomCellRendererProps<SupplierReportListItem>) {
  if (!data || data.revenue_per_booking == null) {
    return "--";
  }

  const trend = data.revenue_per_booking_trend;

  return (
    <span className="flex items-center gap-1.5">
      {formatCurrency(data.revenue_per_booking, Currency.Aud)}
      {trend == null ? null : (
        <span
          className={cn(
            "flex items-center gap-0.5 text-xs",
            trend > 0 ? "text-green-600" : null,
            trend < 0 ? "text-red-600" : null,
            trend === 0 ? "text-gray-500" : null,
          )}
        >
          {trend > 0 ? <ArrowUpIcon className="size-3" /> : null}
          {trend < 0 ? <ArrowDownIcon className="size-3" /> : null}
          {Math.abs(trend).toFixed(1)}%
        </span>
      )}
    </span>
  );
}

const sidebar = {
  toolPanels: ["columns", "filters"],
};

export function SupplierReportTable({ ref }: { ref?: Ref<any> }) {
  const { startDate, endDate } = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/suppliers",
  });
  const { data } = useSuspenseGqlQuery(supplierReportQuery, {
    rangeEnd: endDate,
    rangeStart: startDate,
  });

  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    return {
      mode: "multiRow",
    };
  }, []);

  const columns: ColDef<SupplierReportListItem>[] = useMemo(
    () => [
      {
        field: "supplier_name",
        headerName: "Supplier",
        filter: true,
      },
      {
        field: "charge_per_move_amount",
        headerName: "Charge per Move",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          formatCurrency(params.value, params.data!.currency),
      },
      {
        field: "booking_fee_amount",
        headerName: "Booking Fee",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          formatCurrency(params.value, params.data!.currency),
      },
      {
        field: "revenue_per_booking",
        headerName: "Revenue per Booking",
        headerTooltip:
          "Average AUD revenue per booking over the 3 months to the report end date, against the 3 months before it",
        filter: "agNumberColumnFilter",
        cellRenderer: RevenuePerBookingCell,
      },
      {
        field: "region",
        headerName: "Region",
        filter: true,
        enableRowGroup: true,
      },
      {
        field: "total_relocations",
        headerName: "Total Relocations",
        filter: "agNumberColumnFilter",
      },
      {
        field: "current_listings_count",
        headerName: "Active Relocations",
        filter: "agNumberColumnFilter",
      },
      {
        field: "total_bookings",
        headerName: "Total Bookings",
        filter: "agNumberColumnFilter",
      },
      {
        field: "avg_hours_to_confirm",
        headerName: "Avg Hours to Confirm",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => params.value?.toFixed(1) ?? "N/A",
      },
      {
        field: "last_listing_time",
        headerName: "Last Listing Time",
        filter: true,
      },
      {
        field: "last_listing_time",
        headerName: "Days since last listing",
        filter: true,
        cellRenderer: (params: any) => {
          if (!params.value) return "";

          return Math.abs(dayjs(params.value).diff(dayjs(), "days"));
        },
        sortable: true,
        type: "numericColumn",
      },
      {
        field: "listing_frequency",
        headerName: "Listing Frequency",
        filter: true,
        enableRowGroup: true,
      },
      {
        field: "successful_bookings",
        headerName: "Successful Bookings",
        filter: "agNumberColumnFilter",
      },
      {
        field: "successful_percentage",
        headerName: "Success %",
        filter: "agNumberColumnFilter",
      },
      {
        field: "pending_bookings",
        headerName: "Pending Bookings",
        filter: "agNumberColumnFilter",
        initialHide: true,
      },
      {
        field: "pending_percentage",
        headerName: "Pending %",
        filter: "agNumberColumnFilter",
        initialHide: true,
      },
      {
        field: "supplier_cancelled_bookings",
        headerName: "Supplier Cancelled",
        filter: "agNumberColumnFilter",
      },
      {
        field: "supplier_cancelled_percentage",
        headerName: "Supplier Cancelled %",
        filter: "agNumberColumnFilter",
      },
      {
        field: "customer_cancelled_bookings",
        headerName: "Customer Cancelled",
        filter: "agNumberColumnFilter",
      },
      {
        field: "customer_cancelled_percentage",
        headerName: "Customer Cancelled %",
        filter: "agNumberColumnFilter",
      },
      {
        field: "admin_cancelled_bookings",
        headerName: "Admin Cancelled",
        filter: "agNumberColumnFilter",
      },
      {
        field: "admin_cancelled_percentage",
        headerName: "Admin Cancelled %",
        filter: "agNumberColumnFilter",
      },
      {
        field: "clearance_rate",
        headerName: "Clearance Rate",
        filter: "agNumberColumnFilter",
      },
      {
        field: "avg_weekly_sales",
        headerName: "Weekly Sales",
        filter: "agNumberColumnFilter",
      },
      {
        field: "avg_booking_value",
        headerName: "Avg Booking Value",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatCurrency(params.value, Currency.Aud),
      },
      {
        field: "avg_extra_days",
        headerName: "Avg Extra Days",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => params.value?.toFixed(1) ?? "--",
      },
    ],
    [],
  );

  return (
    <AgGridReact<SupplierReportListItem>
      className="grow"
      ref={ref}
      rowData={data.supplierReport}
      rowSelection={rowSelection}
      columnDefs={columns}
      defaultColDef={defaultColDef}
      enableCharts={true}
      cellSelection={true}
      sideBar={sidebar}
      theme={imoovaTheme}
    />
  );
}
